import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SharePanel from "@/components/feature/SharePanel";
import FavoriteButton from "@/components/feature/FavoriteButton";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/lib/seo";
import { useFavorites } from "@/hooks/useFavorites";
import {
  formatDate,
  getNewsById,
  getRelatedNews,
  getTopicById,
  newsList,
} from "@/lib/media";

export default function NewsDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = getNewsById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const { isNewsFavorite, toggleNews } = useFavorites();

  usePageMeta({
    title: article ? `${article.title} - 影视资讯 - 枫与铃` : "影视资讯 - 枫与铃",
    description: article
      ? article.excerpt
      : "阅读枫与铃影视资讯频道的深度文章与行业动态。",
    keywords: article
      ? `${article.category},影视资讯,${article.title.slice(0, 12)},枫与铃`
      : "影视资讯,枫与铃",
  });

  const related = useMemo(() => (article ? getRelatedNews(article, 3) : []), [article]);
  const topic = useMemo(() => getTopicById("topic-02"), []);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-[72px]">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
            <EmptyState
              icon="ri-article-line"
              title="这篇文章不存在"
              description="可能是链接已经失效，去资讯列表看看最新的内容吧。"
              actionLabel="返回资讯列表"
              onAction={() => navigate("/news")}
            />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorite = isNewsFavorite(article.id);
  const moreNews = newsList
    .filter((item) => item.id !== article.id && item.category === article.category)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50 pb-20 md:pb-0">
      <SiteHeader />

      <main className="pt-[72px]">
        <div className="mx-auto w-full max-w-[1180px] px-4 pb-10 pt-8 md:px-8 md:pt-10">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer hover:text-primary-600">
              首页
            </Link>
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <Link to="/news" className="cursor-pointer hover:text-primary-600">
              影视资讯
            </Link>
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="line-clamp-1 max-w-[200px] text-foreground-800 sm:max-w-none">
              {article.title}
            </span>
          </nav>

          <div className="mt-6 max-w-4xl">
            <span className="rounded bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
              {article.category}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-user-line text-sm" />
                </span>
                {article.author}
              </span>
              <span className="h-3 w-px bg-background-300" />
              <span>{formatDate(article.date)} 发布</span>
              <span className="h-3 w-px bg-background-300" />
              <span>约 4 分钟阅读</span>
            </div>
          </div>

          <div className="mt-7 overflow-hidden rounded-lg bg-background-200">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} 影视资讯`}
              className="aspect-[16/9] w-full object-cover object-top md:aspect-[21/9]"
            />
          </div>

          <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_300px]">
            <article className="min-w-0">
              <p className="text-[15px] font-medium leading-8 text-foreground-800">{article.excerpt}</p>

              <div className="mt-6 flex flex-col gap-5">
                {article.blocks.map((block, index) => {
                  if (block.type === "h2") {
                    return (
                      <h2
                        key={`${block.type}-${index}`}
                        className="mt-3 flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950"
                      >
                        <span className="h-4 w-[3px] rounded-full bg-primary-500" />
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote
                        key={`${block.type}-${index}`}
                        className="rounded-md border-l-2 border-primary-400 bg-background-100 px-5 py-4 text-[15px] leading-7 text-foreground-800"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  if (block.type === "image") {
                    return (
                      <figure key={`${block.type}-${index}`} className="my-2">
                        <div className="overflow-hidden rounded-lg bg-background-200">
                          <img
                            src={block.src}
                            alt={block.caption ?? article.title}
                            title={`${article.title} 配图`}
                            className="aspect-[3/2] w-full object-cover object-top"
                          />
                        </div>
                        {block.caption ? (
                          <figcaption className="mt-2 text-center text-xs text-foreground-500">
                            {block.caption}
                          </figcaption>
                        ) : null}
                      </figure>
                    );
                  }
                  return (
                    <p
                      key={`${block.type}-${index}`}
                      className="text-[15px] leading-8 text-foreground-700"
                    >
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-500">标签</span>
                {[article.category, "影视资讯", "枫与铃编辑"].map((tag) => (
                  <span
                    key={tag}
                    className="rounded bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100/60 px-5 py-4">
                <span className="text-sm text-foreground-600">这篇内容对你有帮助吗？</span>
                <div className="flex items-center gap-2">
                  <FavoriteButton
                    active={favorite}
                    onClick={() => toggleNews(article.id)}
                    label={favorite ? "已收藏这篇" : "收藏这篇"}
                  />
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-share-forward-line text-base" />
                    </span>
                    分享
                  </button>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">
                  相关推荐
                </h2>
                <div className="grid gap-4 sm:grid-cols-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex cursor-pointer flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                    >
                      <span className="text-[11px] text-primary-600">{item.category}</span>
                      <h3 className="mt-2 line-clamp-3 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h3>
                      <span className="mt-auto pt-4 text-[11px] text-foreground-500">
                        {formatDate(item.date)}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </article>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  更多{article.category}
                </h2>
                <ul className="mt-3 flex flex-col">
                  {moreNews.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/${item.id}`}
                        className="group block cursor-pointer border-b border-background-200 py-3 last:border-b-0"
                      >
                        <p className="line-clamp-2 text-xs font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </p>
                        <span className="mt-1 block text-[11px] text-foreground-500">
                          {formatDate(item.date)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {topic ? (
                <Link
                  to={`/topic/${topic.id}`}
                  className="group relative block overflow-hidden rounded-lg bg-foreground-950"
                >
                  <img
                    src={topic.cover}
                    alt={topic.title}
                    title={`${topic.title} 影视专题`}
                    className="h-[160px] w-full object-cover object-top opacity-80 transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <span className="text-[11px] text-accent-300">精选专题</span>
                    <p className="mt-1 font-heading text-base font-semibold text-background-50">
                      {topic.title}
                    </p>
                  </div>
                </Link>
              ) : null}

              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 p-5">
                <p className="text-xs leading-relaxed text-foreground-600">
                  本文为演示内容，人物、作品与事件均为虚构，仅用于展示资讯阅读体验。
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-background-200 bg-background-50/95 px-4 py-3 backdrop-blur md:hidden">
        <button
          type="button"
          onClick={() => toggleNews(article.id)}
          className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-medium ${
            favorite
              ? "border-primary-300 bg-primary-50 text-primary-700"
              : "border-background-300 text-foreground-700"
          }`}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={favorite ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} />
          </span>
          {favorite ? "已收藏" : "收藏"}
        </button>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-share-forward-line text-base" />
          </span>
          分享
        </button>
      </div>

      <SharePanel open={shareOpen} title={article.title} onClose={() => setShareOpen(false)} />
      <SiteFooter />
    </div>
  );
}